import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  toggleOpen: () => void;
  index: number;
}
const FAQItem = ({
  question,
  answer,
  isOpen,
  toggleOpen,
  index
}: FAQItemProps) => {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} className="border-b border-gray-800" data-id="element-33">
      <button onClick={toggleOpen} className="flex justify-between items-center w-full py-4 px-2 text-left focus:outline-none" data-id="element-34">
        <h4 className="text-white font-medium text-base sm:text-lg typekit-heading" data-id="element-35">
          {question}
        </h4>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-36">
          <ChevronDownIcon className="w-5 h-5 text-[#ffdf07]" data-id="element-37" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-38">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-39">
            <div className="pb-4 px-2 text-gray-400 libre-caslon-text-regular" data-id="element-40">
              <p className="leading-relaxed" data-id="element-41">{answer}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
};
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const faqItems = [{
    question: 'Who is this for?',
    answer: 'Owners of private healthcare practices doing $1M - $20M in revenue who are considering selling in the next 2-5 years.'
  }, {
    question: "I'm not selling yet. Why do this now?",
    answer: "Because the work to get top-dollar for your business must start 3-5 years before you sell. Waiting until you're ready is too late and guarantees you'll get a lower price."
  }, {
    question: 'What is the time commitment?',
    answer: 'This is a focused sprint, not a 6-month project. Your main task is providing financials upfront. We give you maximum clarity with minimum disruption.'
  }, {
    question: "Can't my CPA just handle this?",
    answer: "No. Your CPA is for taxes. Preparing a business for a sale to a private equity buyer is a completely different specialty. You wouldn't ask your family doctor to perform open-heart surgery."
  }, {
    question: 'How is this different from hiring a big firm like KPMG?',
    answer: 'They charge $50,000+ just to start. We provide the same Wall Street firepower in a focused sprint specifically for owners like you, at a fraction of the cost.'
  }, {
    question: 'What do I actually get?',
    answer: "Three things: 1) A defensible price range for your clinic and the fastest ways to raise it. 2) A buyer's-eye-view of your business so you can fix red flags. 3) The playbook to defend your price in negotiations."
  }, {
    question: 'What is the guarantee?',
    answer: "If our analysis doesn't uncover at least five times its cost in hidden value, the entire process is free. You keep all the findings."
  }];
  return <section id="faq" className="w-full bg-black py-16 px-4" data-id="element-42">
      <div className="max-w-3xl mx-auto" data-id="element-43">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-44">
          <h2 className="text-white text-3xl md:text-4xl mb-6 typekit-heading" data-id="element-45">
            Frequently Asked Questions
          </h2>
        </motion.div>
        <div className="mt-8" data-id="element-46">
          {faqItems.map((item, index) => <FAQItem key={index} question={item.question} answer={item.answer} isOpen={openIndex === index} toggleOpen={() => toggleFAQ(index)} index={index} data-id="element-47" />)}
        </div>
      </div>
    </section>;
};
export default FAQSection;